import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar, Eye, ArrowRight, Newspaper } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import OptimizedImage from '../components/OptimizedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const NewsArchive = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages?page_type=news&published_only=true`);
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Aktuelt | Sand-tec AS</title>
        <meta name="description" content="Les de siste nyhetene fra Sand-tec AS - EU-kontroll, service og verkstedtjenester" />
      </Helmet>

      {/* Noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #16a34a', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#16a34a', margin: '0 0 8px'}}>Aktuelt — Sand-tec AS</h1>
            <p style={{fontSize: '1.1em', color: '#555', margin: 0}}>Hold deg oppdatert med de siste nyhetene fra Sand-tec AS. Vi deler nyheter om nye tjenester, kampanjer, og viktig informasjon for våre kunder.</p>
          </header>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Hva vi publiserer</h2>
            <ul style={{lineHeight: '2', color: '#374151', paddingLeft: '20px'}}>
              <li>Nye godkjenninger og sertifiseringer</li>
              <li>Utvidelse av tjenestetilbud</li>
              <li>Viktige endringer i regelverk for EU-kontroll</li>
              <li>Sesongbaserte tilbud og kampanjer</li>
              <li>Bransjeoppdateringer for transportnæringen</li>
            </ul>
          </section>

          <section style={{marginBottom: '30px'}}>
            <h2 style={{fontSize: '1.3em', color: '#1f2937', marginBottom: '10px'}}>Om Sand-tec AS</h2>
            <p style={{lineHeight: '1.7', color: '#374151'}}>Sand-tec AS er et godkjent kontrollverksted i Sagstua, Nord-Odal, som spesialiserer seg på EU-kontroll og service for tungbil, tilhenger og personbil. Med kapasitet for 6 store tilhengere samtidig og moderne utstyr, er vi en ledende aktør i regionen for periodisk kjøretøykontroll og verkstedtjenester.</p>
          </section>

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Navigasjon</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/" style={{color: '#16a34a'}}>Forside</a></li>
              <li><a href="/tjenester" style={{color: '#16a34a'}}>Tjenester</a></li>
              <li><a href="/kampanjer" style={{color: '#16a34a'}}>Kampanjer</a></li>
              <li><a href="/referanser" style={{color: '#16a34a'}}>Referanser</a></li>
              <li><a href="/anmeldelser" style={{color: '#16a34a'}}>Anmeldelser</a></li>
              <li><a href="/kontakt" style={{color: '#16a34a'}}>Kontakt</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>Kontakt oss for mer informasjon.</p>
            <p style={{marginTop: '5px'}}>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#16a34a'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#16a34a'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-black">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-green-900/20 pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,197,94,0.1),transparent_50%)]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="bg-green-600/20 text-green-400 border-green-600/50 mb-6 px-4 py-2">
                <Newspaper className="w-4 h-4 inline mr-2" />
                Aktuelt
              </Badge>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-green-400 via-yellow-400 to-green-500 bg-clip-text text-transparent">
                  Siste Nytt
                </span>
              </h1>
              <p className="text-xl text-gray-300">
                Hold deg oppdatert med de siste nyhetene fra Sand-tec AS
              </p>
            </div>
          </div>
        </div>

        {/* News Grid */}
        <div className="container mx-auto px-4 py-20">
          {loading ? (
            <div className="text-center text-gray-400">Laster artikler...</div>
          ) : pages.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <Newspaper className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-xl">Ingen artikler publisert ennå</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {pages.map((page) => (
                <Card
                  key={page.id}
                  onClick={() => navigate(`/aktuelt/${page.slug}`)}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 hover:border-green-600/50 cursor-pointer transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-green-500/20 group"
                >
                  {page.featured_image && (
                    <div className="w-full h-48 overflow-hidden rounded-t-lg">
                      <OptimizedImage
                        src={page.featured_image}
                        alt={page.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center space-x-4 text-sm text-gray-400 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(page.created_at).toLocaleDateString('no-NO', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</span>
                      </div>
                      {page.views > 0 && (
                        <div className="flex items-center space-x-1">
                          <Eye className="w-4 h-4" />
                          <span>{page.views}</span>
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-white group-hover:text-green-400 transition-colors line-clamp-2">
                      {page.title}
                    </CardTitle>
                    {page.excerpt && (
                      <CardDescription className="text-gray-400 line-clamp-3 mt-2">
                        {page.excerpt}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-green-400 group-hover:text-green-300 font-semibold">
                      <span>Les mer</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NewsArchive;
